import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von09Infections } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von09InfectionsFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von09InfectionsForm: React.FC<Von09InfectionsFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  // Congenital infections
  const [congenInf, setCongenInf] = useState(false);
  const [congenInfCd1, setCongenInfCd1] = useState<string>('');
  const [congenInfCd2, setCongenInfCd2] = useState<string>('');
  const [congenInfCd3, setCongenInfCd3] = useState<string>('');

  // Early onset sepsis
  const [eosBactMeningitis, setEosBactMeningitis] = useState(false);
  const [eosPathogen1, setEosPathogen1] = useState<string>('');
  const [eosPathogen2, setEosPathogen2] = useState<string>('');
  const [eosPathogen3, setEosPathogen3] = useState<string>('');

  // Late onset sepsis
  const [losBactMeningitis, setLosBactMeningitis] = useState(false);
  const [losWhere, setLosWhere] = useState<string>('');
  const [losPathogen1, setLosPathogen1] = useState<string>('');
  const [losPathogen2, setLosPathogen2] = useState<string>('');
  const [losPathogen3, setLosPathogen3] = useState<string>('');

  // Specific infections
  const [consAfter3, setConsAfter3] = useState(false);
  const [consAfter3Where, setConsAfter3Where] = useState<string>('');
  const [fungalAfter3, setFungalAfter3] = useState(false);
  const [fungalAfter3Where, setFungalAfter3Where] = useState<string>('');

  // Sepsis pathogen
  const [sepsisPathogen, setSepsisPathogen] = useState<string>('');
  const [sepsisPathogenOther, setSepsisPathogenOther] = useState<string>('');

  // Meningitis
  const [meningitisHydroShuntDate, setMeningitisHydroShuntDate] = useState<string>('');
  const [csfCultureResult, setCsfCultureResult] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getInfections(patientId);

      if (data) {
        setHasExistingData(true);
        setCongenInf(Boolean(data.congen_inf));
        setCongenInfCd1(data.congen_inf_cd1 || '');
        setCongenInfCd2(data.congen_inf_cd2 || '');
        setCongenInfCd3(data.congen_inf_cd3 || '');
        setEosBactMeningitis(Boolean(data.eos_bact_meningitis));
        setEosPathogen1(data.eos_pathogen1 || '');
        setEosPathogen2(data.eos_pathogen2 || '');
        setEosPathogen3(data.eos_pathogen3 || '');
        setLosBactMeningitis(Boolean(data.los_bact_meningitis));
        setLosWhere(data.los_where?.toString() || '');
        setLosPathogen1(data.los_pathogen1 || '');
        setLosPathogen2(data.los_pathogen2 || '');
        setLosPathogen3(data.los_pathogen3 || '');
        setConsAfter3(Boolean(data.cons_after3));
        setConsAfter3Where(data.cons_after3_where?.toString() || '');
        setFungalAfter3(Boolean(data.fungal_after3));
        setFungalAfter3Where(data.fungal_after3_where?.toString() || '');
        setSepsisPathogen(data.sepsis_pathogen?.toString() || '');
        setSepsisPathogenOther(data.sepsis_pathogen_other || '');
        setMeningitisHydroShuntDate(data.meningitis_hydro_shunt_date || '');
        setCsfCultureResult(data.csf_culture_result?.toString() || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von09Infections> = {
        patient_id: patientId,
        congen_inf: congenInf,
        congen_inf_cd1: congenInfCd1 || undefined,
        congen_inf_cd2: congenInfCd2 || undefined,
        congen_inf_cd3: congenInfCd3 || undefined,
        eos_bact_meningitis: eosBactMeningitis,
        eos_pathogen1: eosPathogen1 || undefined,
        eos_pathogen2: eosPathogen2 || undefined,
        eos_pathogen3: eosPathogen3 || undefined,
        los_bact_meningitis: losBactMeningitis,
        los_where: losWhere ? parseInt(losWhere) : undefined,
        los_pathogen1: losPathogen1 || undefined,
        los_pathogen2: losPathogen2 || undefined,
        los_pathogen3: losPathogen3 || undefined,
        cons_after3: consAfter3,
        cons_after3_where: consAfter3Where ? parseInt(consAfter3Where) : undefined,
        fungal_after3: fungalAfter3,
        fungal_after3_where: fungalAfter3Where ? parseInt(fungalAfter3Where) : undefined,
        sepsis_pathogen: sepsisPathogen ? parseInt(sepsisPathogen) : undefined,
        sepsis_pathogen_other: sepsisPathogenOther || undefined,
        meningitis_hydro_shunt_date: meningitisHydroShuntDate || undefined,
        csf_culture_result: csfCultureResult ? parseInt(csfCultureResult) : undefined
      };

      if (hasExistingData) {
        await vonDataService.updateInfections(patientId, formData);
      } else {
        await vonDataService.createInfections(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 10: Infecții</CardTitle>
        <CardDescription>Date despre infecții congenitale, sepsis și meningită</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Congenital infections */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Infecții congenitale</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="congenInf"
                checked={congenInf}
                onCheckedChange={(checked) => setCongenInf(checked === true)}
                disabled={readOnly}
              />
              <Label htmlFor="congenInf" className="text-sm font-normal cursor-pointer">
                Infecție congenitală
              </Label>
            </div>

            {congenInf && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="congenInfCd1">Cod infecție congenitală 1</Label>
                  <Input
                    id="congenInfCd1"
                    value={congenInfCd1}
                    onChange={(e) => setCongenInfCd1(e.target.value)}
                    placeholder="Ex: Toxoplasmoză"
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="congenInfCd2">Cod infecție congenitală 2</Label>
                  <Input
                    id="congenInfCd2"
                    value={congenInfCd2}
                    onChange={(e) => setCongenInfCd2(e.target.value)}
                    placeholder="Ex: Rubeolă"
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="congenInfCd3">Cod infecție congenitală 3</Label>
                  <Input
                    id="congenInfCd3"
                    value={congenInfCd3}
                    onChange={(e) => setCongenInfCd3(e.target.value)}
                    placeholder="Ex: CMV"
                    disabled={readOnly}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Early onset sepsis */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Sepsis precoce (≤ ziua 3)</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="eosBactMeningitis"
                checked={eosBactMeningitis}
                onCheckedChange={(checked) => setEosBactMeningitis(checked === true)}
                disabled={readOnly}
              />
              <Label htmlFor="eosBactMeningitis" className="text-sm font-normal cursor-pointer">
                Sepsis/meningită bacteriană în/înainte de ziua 3
              </Label>
            </div>

            {eosBactMeningitis && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="eosPathogen1">Patogen 1</Label>
                  <Input
                    id="eosPathogen1"
                    value={eosPathogen1}
                    onChange={(e) => setEosPathogen1(e.target.value)}
                    placeholder="Ex: E. coli"
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eosPathogen2">Patogen 2</Label>
                  <Input
                    id="eosPathogen2"
                    value={eosPathogen2}
                    onChange={(e) => setEosPathogen2(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eosPathogen3">Patogen 3</Label>
                  <Input
                    id="eosPathogen3"
                    value={eosPathogen3}
                    onChange={(e) => setEosPathogen3(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Late onset sepsis */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Sepsis tardiv ({'>'} ziua 3)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="losBactMeningitis"
                  checked={losBactMeningitis}
                  onCheckedChange={(checked) => setLosBactMeningitis(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="losBactMeningitis" className="text-sm font-normal cursor-pointer">
                  Sepsis/meningită bacteriană după ziua 3
                </Label>
              </div>

              {losBactMeningitis && (
                <div className="space-y-2">
                  <Label htmlFor="losWhere">Unde a apărut</Label>
                  <Select value={losWhere} onValueChange={setLosWhere} disabled={readOnly}>
                    <SelectTrigger id="losWhere">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Spitalul dumneavoastră</SelectItem>
                      <SelectItem value="2">În afară</SelectItem>
                      <SelectItem value="3">Ambele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {losBactMeningitis && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="losPathogen1">Patogen 1</Label>
                  <Input
                    id="losPathogen1"
                    value={losPathogen1}
                    onChange={(e) => setLosPathogen1(e.target.value)}
                    placeholder="Ex: Staph. aureus"
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="losPathogen2">Patogen 2</Label>
                  <Input
                    id="losPathogen2"
                    value={losPathogen2}
                    onChange={(e) => setLosPathogen2(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="losPathogen3">Patogen 3</Label>
                  <Input
                    id="losPathogen3"
                    value={losPathogen3}
                    onChange={(e) => setLosPathogen3(e.target.value)}
                    disabled={readOnly}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Specific infections */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Infecții specifice după ziua 3</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="consAfter3"
                  checked={consAfter3}
                  onCheckedChange={(checked) => setConsAfter3(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="consAfter3" className="text-sm font-normal cursor-pointer">
                  Stafilococ coagulazo-negativ după ziua 3
                </Label>
              </div>

              {consAfter3 && (
                <div className="space-y-2">
                  <Label htmlFor="consAfter3Where">Unde a apărut</Label>
                  <Select value={consAfter3Where} onValueChange={setConsAfter3Where} disabled={readOnly}>
                    <SelectTrigger id="consAfter3Where">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Spitalul dumneavoastră</SelectItem>
                      <SelectItem value="2">În afară</SelectItem>
                      <SelectItem value="3">Ambele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="fungalAfter3"
                  checked={fungalAfter3}
                  onCheckedChange={(checked) => setFungalAfter3(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="fungalAfter3" className="text-sm font-normal cursor-pointer">
                  Infecție fungică după ziua 3
                </Label>
              </div>

              {fungalAfter3 && (
                <div className="space-y-2">
                  <Label htmlFor="fungalAfter3Where">Unde a apărut</Label>
                  <Select value={fungalAfter3Where} onValueChange={setFungalAfter3Where} disabled={readOnly}>
                    <SelectTrigger id="fungalAfter3Where">
                      <SelectValue placeholder="Selectează..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Spitalul dumneavoastră</SelectItem>
                      <SelectItem value="2">În afară</SelectItem>
                      <SelectItem value="3">Ambele</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Sepsis pathogen */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Patogen sepsis identificat</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sepsisPathogen">Organism identificat (codat 1-18, 99=Altul)</Label>
                <Input
                  id="sepsisPathogen"
                  type="number"
                  min="1"
                  max="99"
                  value={sepsisPathogen}
                  onChange={(e) => setSepsisPathogen(e.target.value)}
                  placeholder="Cod 1-18 sau 99"
                  disabled={readOnly}
                />
              </div>

              {sepsisPathogen === '99' && (
                <div className="space-y-2">
                  <Label htmlFor="sepsisPathogenOther">Alt organism (text)</Label>
                  <Textarea
                    id="sepsisPathogenOther"
                    value={sepsisPathogenOther}
                    onChange={(e) => setSepsisPathogenOther(e.target.value)}
                    placeholder="Descriere organism"
                    disabled={readOnly}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Meningitis details */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Detalii meningită</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="meningitisShuntDate">Data procedură shunt</Label>
                <Input
                  id="meningitisShuntDate"
                  type="date"
                  value={meningitisHydroShuntDate}
                  onChange={(e) => setMeningitisHydroShuntDate(e.target.value)}
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="csfCultureResult">Rezultat cultură LCR</Label>
                <Select value={csfCultureResult} onValueChange={setCsfCultureResult} disabled={readOnly}>
                  <SelectTrigger id="csfCultureResult">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Pozitiv</SelectItem>
                    <SelectItem value="2">Negativ</SelectItem>
                    <SelectItem value="3">Nu s-a efectuat</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
